import styled from '@emotion/styled';
import { isEmptyString } from '@racemap/utilities/functions/utils';
import { Alert, Button, Flex, Result, Space, Typography } from 'antd';
import type { ResultStatusType } from 'antd/es/result';
import React, { useEffect, useState, type FC } from 'react';
import {
  type NavigateFunction,
  isRouteErrorResponse,
  useLocation,
  useNavigate,
  useRouteError,
} from 'react-router-dom';
import shortHash from 'shorthash2';
import { type ExtendedError, UnauthorizedError } from 'src/lib/Errors';
import { sendErrorEvent } from 'src/lib/googleAnalytics';
import { clearSourceMapCache, mapStackTrace, parseStackLine } from './stackUtils';

const { Text, Link } = Typography;

export const ErrorPage: FC = () => {
  const error = useRouteError();
  const navigate = useNavigate();
  console.error(error);

  useEffect(() => {
    sendErrorEvent(error, true);
  }, [error]);

  useEffect(() => {
    // Clear source map cache on unmount to free memory
    return clearSourceMapCache();
  }, []);

  if (isRouteErrorResponse(error)) {
    return (
      <Result
        status={error.status as ResultStatusType}
        title={error.statusText}
        subTitle={error.data?.message || subTitles[error.status as ResultStatusType]}
        extra={getExtra(error.status as ResultStatusType, navigate)}
      />
    );
  }
  if (error instanceof UnauthorizedError) {
    return (
      <Result
        status={403}
        title={error.name}
        subTitle={error.message}
        extra={getExtra(403, navigate)}
      />
    );
  }
  if (error instanceof Error) {
    return (
      <Result
        status="error"
        title={error.name}
        subTitle={error.message}
        extra={getExtra('error', navigate, error)}
      />
    );
  }
  return (
    <Result
      status="error"
      title="An error occurred"
      subTitle="Unknown error. Please try again later."
      extra={getExtra('error', navigate)}
    />
  );
};

const subTitles: Record<ResultStatusType, string> = {
  403: 'You are not authorized to view this page',
  404: 'The requested page could not be found',
  500: 'An unexpected error occurred',
  info: 'There was an informational message',
  success: 'The operation was successful',
  error: 'An error occurred',
  warning: 'There was a warning',
};

const getExtra = (errorStatus: ResultStatusType, navigate: NavigateFunction, error?: Error) => {
  switch (errorStatus) {
    case 403:
    case 404:
    case 500:
      return (
        <Button type="primary" onClick={() => navigate('/admin')}>
          Back Home
        </Button>
      );
    default:
      if (error) {
        return (
          <Flex justify="center">
            <Alert type="error" message={<ErrorMessage error={error} />} />
          </Flex>
        );
      }

      return (
        <Button type="primary" onClick={() => navigate('/admin')}>
          Back Home
        </Button>
      );
  }
};

const ErrorMessage: FC<{ error: Error }> = ({ error }) => {
  const { message, stack } = error;
  const metadata = 'metadata' in error ? (error as ExtendedError).metadata : undefined;
  const [mappedStack, setMappedStack] = useState<Array<string>>([]);
  const location = useLocation();

  useEffect(() => {
    if (stack) {
      mapStackTrace(stack).then(setMappedStack);
    }
  }, [stack]);

  const stackLines = mappedStack.length > 0 ? mappedStack : stack?.split('\n') || [];
  const subject = encodeURI('Frontend Error');
  const body = encodeURI(getTextVersionOfError(error, mappedStack.join('\n')));

  return (
    <StackBox vertical gap={15} align="flex-start">
      <InfoText>
        We apologize for the inconvenience caused by the software issue and try to fix that problem
        as fast as possible. You can help us, if you send us the content of that panel to our
        support mail{' '}
        <Link href={`mailto:support@racemap.com?subject=${subject}&body=${body}`}>
          support@racemap.com
        </Link>
        .
      </InfoText>
      <ErrorMessageContainer>
        <Space>
          <Text strong>Message:</Text> <Text>{message}</Text>
        </Space>
        <Space>
          <Text strong>Current URL:</Text> <Text>{window.location.origin + location.pathname}</Text>
        </Space>
        <Space>
          <Text strong>Occurred At:</Text> <Text>{new Date().toISOString()}</Text>
        </Space>

        <ErrorMetadata metadata={metadata} />
      </ErrorMessageContainer>
      <Text strong>Error stack:</Text>
      <StackEntries>
        {stackLines.map((line, index) => {
          if (isEmptyString(line)) return null;

          const parsed = parseStackLine(line);
          if (!parsed) return null;

          return (
            <React.Fragment key={shortHash(line)}>
              <LineNumber>{index}</LineNumber>
              <ErrorMethod strong>{parsed.method}</ErrorMethod>
              <Text>{parsed.file}</Text>
            </React.Fragment>
          );
        })}
      </StackEntries>
    </StackBox>
  );
};

const ErrorMetadata: FC<{ metadata?: Record<string, any> }> = ({ metadata }) => {
  if (!metadata) return null;

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Text strong>Metadata:</Text>
      {Object.entries(metadata).map(([key, value]) => {
        const stringValue = String(JSON.stringify(value));
        const displayValue =
          stringValue.length > 200 ? `${stringValue.substring(0, 200)}...` : stringValue;
        return (
          <Space key={key}>
            <Text strong>{key}:</Text> <Text>{displayValue}</Text>
          </Space>
        );
      })}
    </Space>
  );
};

const getTextVersionOfError = (error: Error, mappedStack?: string) => {
  const { message, stack } = error;
  const now = new Date().toISOString();
  const metadata = 'metadata' in error ? (error as ExtendedError).metadata : undefined;

  return `
    Message: ${message}
    Current URL: ${window.location.origin + window.location.pathname}
    Occurred at: ${now}
    ${getMetadataString(metadata)}

    Error stack:
    ${mappedStack || stack}
  `;
};

const getMetadataString = (metadata?: Record<string, any>) => {
  if (!metadata) return '';

  return Object.entries(metadata)
    .map(([key, value]) => `    ${key}: ${JSON.stringify(value)}`)
    .join('\n');
};

const StackBox = styled(Flex)`
    text-align: start;
    max-height: 50vh;
    overflow: auto;
    padding: 10px;
    white-space: nowrap;
    max-width: 900px;
`;

const StackEntries = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: 3ch 40ch 1fr;
    gap: 5px;
    overflow: auto;
    padding: 10px;
    white-space: nowrap;
    font-family: monospace;
    text-align: start;
    overflow: auto;
`;

const ErrorMessageContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const LineNumber = styled(Text)`
    font-family: monospace;
    white-space: nowrap;
    overflow: auto;
`;

const ErrorMethod = styled(Text)`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const InfoText = styled(Text)`
    white-space: normal;
    overflow: unset;
`;
